import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { estruturacaoConsultorService } from '@/services/estruturacaoConsultorService'
import { documentoService } from '@/services/documentoService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import { Documento } from '@/types/documento'
import { ChecklistReserva } from '@/types/estruturacao'
import {
  ArquivoPreviewDialog,
  ChecklistComArquivos,
  useArquivoReserva,
} from '@/components/documentos/ChecklistComArquivos'

/**
 * Documentos da reserva para a curadoria na Estruturação do back-office (relato de teste do
 * usuário, 14/10/2026): "na tela de aprovar a reserva eu consigo acessar os documentos, mas na
 * Estruturação, para iniciar a curadoria da operação, não consigo". Mesmo checklist com os
 * arquivos dentro de cada item, com visualizar e baixar, da Validação de Reservas. O envio de
 * documento continua no diálogo de upload, aberto pelo botão "Enviar documentos".
 */
export const DocumentosReservaDialog: FC<{
  open: boolean
  reservaId: string | null
  titulo: string
  onClose: () => void
  onEnviar?: () => void
}> = ({ open, reservaId, titulo, onClose, onEnviar }) => {
  const [checklist, setChecklist] = useState<ChecklistReserva | null>(null)
  const [documentos, setDocumentos] = useState<Documento[]>([])
  const [carregando, setCarregando] = useState(false)
  const [avisos, setAvisos] = useState<string[]>([])
  const { abrirArquivo, preview, fecharPreview, ocupadoId } = useArquivoReserva()

  useEffect(() => {
    if (!open || !reservaId) return
    let ativo = true
    setCarregando(true)
    setAvisos([])
    const novosAvisos: string[] = []
    Promise.all([
      estruturacaoConsultorService.checklist(reservaId).catch(() => {
        novosAvisos.push('Checklist de documentos indisponível.')
        return null
      }),
      documentoService
        .listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, reservaId, 0, 500)
        .then((p) => p.content ?? [])
        .catch(() => {
          novosAvisos.push('Lista de arquivos indisponível.')
          return [] as Documento[]
        }),
    ])
      .then(([ck, docs]) => {
        if (!ativo) return
        setChecklist(ck)
        setDocumentos(docs)
        setAvisos(novosAvisos)
      })
      .finally(() => {
        if (ativo) setCarregando(false)
      })
    return () => {
      ativo = false
    }
  }, [open, reservaId])

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Documentos da reserva
          <Typography variant="body2" color="text.secondary">
            {titulo}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} aria-label="Carregando documentos" />
            </Box>
          ) : (
            <>
              {avisos.map((a) => (
                <Alert key={a} severity="warning" sx={{ mb: 2 }}>
                  {a}
                </Alert>
              ))}
              <ChecklistComArquivos
                checklist={checklist}
                documentos={documentos}
                ocupadoId={ocupadoId}
                onAbrir={(d, download) => void abrirArquivo(d, download)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {onEnviar && (
            <Button startIcon={<UploadFileIcon />} onClick={onEnviar}>
              Enviar documentos
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
      <ArquivoPreviewDialog preview={preview} onFechar={fecharPreview} onBaixar={(d) => void abrirArquivo(d, true)} />
    </>
  )
}

export default DocumentosReservaDialog
